import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mailbox, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-10 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-white">
            <Mailbox className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tracking-tighter leading-none">MailShift</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Migration Engine</p>
          </div>
        </div>

        {!token ? (
          <div className="space-y-4">
            <h1 className="font-heading text-3xl font-bold tracking-tighter">Invalid link</h1>
            <p className="text-muted-foreground">This reset link is missing its token. Request a new one.</p>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="space-y-5">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <h1 className="font-heading text-3xl font-bold tracking-tighter">Password updated</h1>
            <p className="text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold tracking-tighter">Set a new password</h1>
            <p className="mt-3 text-muted-foreground">Choose a strong new password for your account.</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="pw" className="text-xs uppercase tracking-[0.15em]">New password</Label>
                <Input id="pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-xs uppercase tracking-[0.15em]">Confirm password</Label>
                <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="reset-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Update password
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
